import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useState } from 'react';
import { Pressable, ScrollView, TextInput, View } from 'react-native';

import { ActivityIndicator, Button, Modal, Text, useTheme } from '@/components/ui';
import { useAccountSearch } from '@/hooks/use-data';

const useDebounced = (value: string, delayMs: number): string => {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const t = setTimeout(() => setDebounced(value), delayMs);
		return () => clearTimeout(t);
	}, [value, delayMs]);
	return debounced;
};

export interface AssignLotModalProps {
	visible: boolean;
	/** The association exactly as the homeowner typed it — seeds the search. */
	associationNameRaw?: string;
	/** A property address off the message, a better seed than the association name. */
	propertyAddress?: string;
	/** True when a lot is already assigned, so the copy says "change" not "assign". */
	hasLot: boolean;
	busy?: boolean;
	onDismiss: () => void;
	onAssign: (accountId: string | null) => void;
}

/**
 * Assign — or correct — the lot on ANY message (#369).
 *
 * ONE search box, like the global search: type an owner name, an address or an account
 * number and pick from a dropdown that shows all three plus the association. It rides the
 * global search endpoint for exactly that reason — a scoped account typeahead would have
 * forced the association to be chosen first, which is two decisions for what a staffer
 * experiences as one.
 *
 * Seeded with the address off the message, so the common case is: open, glance, pick.
 */
export const AssignLotModal: React.FC<AssignLotModalProps> = ({
	visible,
	associationNameRaw,
	propertyAddress,
	hasLot,
	busy = false,
	onDismiss,
	onAssign,
}) => {
	const theme = useTheme();
	const [query, setQuery] = useState('');
	const debounced = useDebounced(query, 250);

	// Re-seed each time it opens: the message may have changed underneath.
	useEffect(() => {
		if (visible) setQuery(propertyAddress ?? associationNameRaw ?? '');
	}, [visible, propertyAddress, associationNameRaw]);

	const { data: results, isFetching } = useAccountSearch(debounced, { enabled: visible });

	return (
		<Modal visible={visible} onDismiss={onDismiss}>
			<View style={{ padding: 20, minWidth: 380, maxWidth: 560 }}>
				<Text style={{ fontSize: 18, fontWeight: '700', marginBottom: 4 }}>
					{hasLot ? 'Change the lot' : 'Assign a lot'}
				</Text>
				<Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 12 }}>
					Search by owner name, address or account number.
				</Text>

				<View
					style={{
						flexDirection: 'row',
						alignItems: 'center',
						borderWidth: 1,
						borderColor: theme.colors.outlineVariant ?? theme.colors.outline,
						borderRadius: 8,
						paddingHorizontal: 10,
					}}
				>
					<Ionicons name="search" size={16} color={theme.colors.onSurfaceVariant} style={{ marginRight: 8 }} />
					<TextInput
						value={query}
						onChangeText={setQuery}
						placeholder="e.g. Frere, 3277 Natal Lane, or 3277NL"
						placeholderTextColor={theme.colors.onSurfaceVariant}
						autoFocus
						testID="assign-lot-search"
						style={{ flex: 1, paddingVertical: 10, color: theme.colors.onSurface }}
					/>
					{isFetching ? <ActivityIndicator size="small" /> : null}
				</View>

				<ScrollView style={{ maxHeight: 320, marginTop: 8 }}>
					{debounced.trim().length < 2 ? (
						<Text style={{ color: theme.colors.onSurfaceVariant, padding: 12 }}>Type at least two characters.</Text>
					) : (results ?? []).length === 0 && !isFetching ? (
						<Text style={{ color: theme.colors.onSurfaceVariant, padding: 12 }}>No lots match that.</Text>
					) : (
						(results ?? []).map((account) => (
							<Pressable
								key={account.id}
								onPress={() => onAssign(account.id)}
								disabled={busy}
								testID={`assign-lot-result-${account.id}`}
								// Real padding, not hitSlop — RN-Web's Pressable drops hitSlop.
								style={{
									paddingVertical: 10,
									paddingHorizontal: 10,
									borderRadius: 8,
									borderBottomWidth: 1,
									borderBottomColor: theme.colors.outlineVariant ?? theme.colors.outline,
								}}
							>
								<View style={{ flexDirection: 'row', alignItems: 'center' }}>
									<Text style={{ fontWeight: '700', flex: 1, minWidth: 0 }} numberOfLines={1}>
										{account.ownerNames || account.address || 'Lot'}
									</Text>
									{account.accountNumber ? (
										<Text style={{ fontSize: 12, fontWeight: '600', color: theme.colors.primary, marginLeft: 8 }}>
											{account.accountNumber}
										</Text>
									) : null}
								</View>
								{/* Address AND association on every row: an owner name alone is
									 ambiguous across a 55-association portfolio. */}
								{account.address ? (
									<Text style={{ color: theme.colors.onSurfaceVariant }} numberOfLines={1}>{account.address}</Text>
								) : null}
								{account.associationName ? (
									<Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant }} numberOfLines={1}>
										{account.associationName}
									</Text>
								) : null}
							</Pressable>
						))
					)}
				</ScrollView>

				<View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
					{/* Clearing has to be possible: a WRONG match must be correctable, not just
						 replaceable — sometimes the right answer is "we don't know yet". */}
					{hasLot ? (
						<Button mode="outlined" variant="error" onPress={() => onAssign(null)} disabled={busy} testID="btn-clear-lot">
							Clear
						</Button>
					) : <View />}
					<Button mode="outlined" onPress={onDismiss} disabled={busy}>Cancel</Button>
				</View>
			</View>
		</Modal>
	);
};

export default AssignLotModal;
